import ChevronRightOutlinedIcon from '@mui/icons-material/ChevronRightOutlined'
import stripHtml from '../../utils/stripHtml'

// AI-CONTEXT: See docs/features/livereport-widgets-housevisits-personas.md
// Shared feed of `PersonaReports`. Used by both `DashboardPersonaReportsCard`
// (dashboard) and the upcoming `PersonasStoriesWidget` (live-report).
//
// Caller passes already-filtered + sorted reports; this component only
// slices to `maxItems` and renders the cards. Anonymise masks persona name
// and reporting-user name/avatar — the narrative body itself is preserved
// (v1 limitation, documented in the spec).
//
// Click behaviour goes through the `onItemClick(report)` slot; dashboard
// passes a navigation handler, widget passes nothing so the item is inert.

const DEFAULT_MAX_ITEMS = 10
const ANONYMISED_LABEL = 'Deelnemer'

const dateFormatter = new Intl.DateTimeFormat('nl-NL', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
})

const truncate = (text, n = 90) =>
    text && text.length > n ? `${text.substring(0, n).trim()}…` : text

/**
 * @typedef {object} PersonaReportDoc
 * @property {string} ID
 * @property {string} PersonaID
 * @property {string} [User] - The reporter's user ID
 * @property {string} [Title]
 * @property {string} [Body]
 * @property {{ toDate: () => Date }} [Timestamp]
 */

/**
 * @param {object} props
 * @param {Array<PersonaReportDoc>} props.reports - Already filtered + sorted by the caller
 * @param {Object<string, { ID: string, Name?: string }>} [props.personaById] - Lookup
 * @param {Object<string, { ID: string, ForName?: string, Photo?: string }>} [props.userById]
 * @param {number} [props.maxItems=10] - Slice to first N reports
 * @param {boolean} [props.anonymise=false] - Mask persona + reporter identity
 * @param {(report: PersonaReportDoc) => void} [props.onItemClick] - Click slot; dashboard wires, widget omits
 * @param {string} [props.emptyText='Nog geen rapporten.']
 * @returns {JSX.Element}
 */
const PersonasStoriesVisualization = ({
    reports,
    personaById,
    userById,
    maxItems = DEFAULT_MAX_ITEMS,
    anonymise = false,
    onItemClick,
    emptyText = 'Nog geen rapporten.',
}) => {
    const list = Array.isArray(reports) ? reports.slice(0, maxItems) : []

    if (list.length === 0) {
        return <p className="persona-reports-empty">{emptyText}</p>
    }

    return (
        <ul className="persona-reports-feed">
            {list.map((report) => {
                const persona = personaById?.[report.PersonaID]
                const user = userById?.[report.User]
                const body = truncate(stripHtml(report.Body))
                const date = report.Timestamp?.toDate
                    ? dateFormatter.format(report.Timestamp.toDate())
                    : ''

                const personaName = anonymise ? ANONYMISED_LABEL : (persona?.Name || '—')
                const reporterName = anonymise ? ANONYMISED_LABEL : (user?.ForName || '—')
                const showPhoto = !anonymise && Boolean(user?.Photo)

                const clickable = Boolean(onItemClick)
                const handleClick = clickable ? () => onItemClick(report) : undefined

                return (
                    <li
                        key={report.ID}
                        className={`persona-reports-item${clickable ? '' : ' persona-reports-item-static'}`}
                        onClick={handleClick}
                        data-anonymised={anonymise ? 'yes' : 'no'}
                    >
                        <div className="persona-reports-item-avatar">
                            {showPhoto && <img src={user.Photo} alt={user.ForName || ''} />}
                        </div>

                        <div className="persona-reports-item-body">
                            <div className="persona-reports-item-top">
                                <h4 className="persona-reports-item-title">
                                    {report.Title || 'Naamloos rapport'}
                                </h4>
                                <span className="persona-reports-item-date">{date}</span>
                            </div>

                            <div className="persona-reports-item-meta">
                                <span>{personaName}</span>
                                <span className="persona-reports-item-meta-sep">·</span>
                                <span>{reporterName}</span>
                            </div>

                            {body && (
                                <p className="persona-reports-item-snippet">{body}</p>
                            )}
                        </div>

                        {clickable && (
                            <ChevronRightOutlinedIcon className="persona-reports-item-chevron" />
                        )}
                    </li>
                )
            })}
        </ul>
    )
}

export default PersonasStoriesVisualization
